import { RoadmapFeature, RoadmapFeatureFilters, SortCriteria } from "../types";

export interface getFilteredAndSortedRoadmapFeaturesArgs {
    features: RoadmapFeature[];
    filters: RoadmapFeatureFilters;
    sortCriteria: SortCriteria | undefined;
}

export function getFilteredAndSortedRoadmapFeatures(
    args: getFilteredAndSortedRoadmapFeaturesArgs
): RoadmapFeature[] {
    const { features, filters, sortCriteria } = args;

    let filtered = features.filter((feature) => {
        return (
            matchesCategory(feature, filters.category) &&
            matchesSelectedProduct(feature, filters.selectedProduct) &&
            matchesSearch(feature, filters.search) &&
            matchesStatus(feature, filters.status)
        );
    });

    if (sortCriteria) {
        filtered = [...filtered].sort((a, b) =>
            compareFeatures(a, b, sortCriteria)
        );
    }

    return filtered;
}

function extractTextFromJson(json: unknown): string {
    if (typeof json === "string") {
        return json;
    }
    if (typeof json === "number" || typeof json === "boolean") {
        return String(json);
    }
    if (json === null || json === undefined) {
        return "";
    }
    if (Array.isArray(json)) {
        return json.map((item) => extractTextFromJson(item)).join(" ");
    }
    if (typeof json === "object") {
        return Object.values(json)
            .map((value) => extractTextFromJson(value))
            .join(" ");
    }
    return "";
}

function matchesSearch(
    feature: RoadmapFeature,
    searchTerms: string[]
): boolean {
    if (searchTerms.length === 0) {
        return true;
    }

    const titleText = feature.title.toLowerCase();
    const descriptionText = extractTextFromJson(
        feature.description.json
    ).toLowerCase();
    const aggregateText = `${titleText} ${descriptionText}`;

    return searchTerms.every((term) =>
        aggregateText.includes(term.toLowerCase())
    );
}

function matchesCategory(
    feature: RoadmapFeature,
    categories: string[]
): boolean {
    if (categories.length === 0) {
        return true;
    }
    return categories.includes(feature.category.queryParameter);
}

function matchesSelectedProduct(
    feature: RoadmapFeature,
    selectedProducts: string[]
): boolean {
    if (selectedProducts.length === 0) {
        return true;
    }
    return feature.appsCollection.items.some((app) =>
        selectedProducts.includes(app.filterQueryParam)
    );
}

function matchesStatus(feature: RoadmapFeature, statuses: string[]): boolean {
    if (statuses.length === 0) {
        return true;
    }
    return statuses.includes(feature.status.roadmapStatusNameExternal);
}

function compareFeatures(
    a: RoadmapFeature,
    b: RoadmapFeature,
    sortCriteria: SortCriteria
): number {
    const { key, direction } = sortCriteria;
    const multiplier = direction === "asc" ? 1 : -1;

    let aValue: string;
    let bValue: string;

    switch (key) {
        case "title":
            aValue = a.title;
            bValue = b.title;
            break;
        case "category":
            aValue = a.category.roadmapCategoryNameExternal;
            bValue = b.category.roadmapCategoryNameExternal;
            break;
        case "status":
            aValue = a.status.roadmapStatusNameExternal;
            bValue = b.status.roadmapStatusNameExternal;
            break;
        case "timeFrame":
            aValue = a.timeFrame;
            bValue = b.timeFrame;
            break;
        default:
            return 0;
    }

    if (aValue < bValue) {
        return -1 * multiplier;
    }
    if (aValue > bValue) {
        return 1 * multiplier;
    }
    return 0;
}
